import type { OnlineStateEnum, InventoryStatusEnum } from 'fleet-data-model';
import type {
  AggregatedCountsResponse,
  AttentionListResponse,
  DashboardSettingsResponse,
  DashboardFirmwareUpdatesResponse,
  DashboardServiceRemindersResponse,
  DashboardSecondaryStatusCountResponse,
  UpdateDashboardSettingsRequestBody,
  DashboardLastSeenSummaryResponse,
  MachineOperationalStatusCountResponse,
  NpsResponse,
  UserNpsFeedback,
} from '@fleet-bffs/api-contract';
import { bffWebApi, TagTypes } from '~/api';
import { transformAppGatewaysChartData } from '../utils/response-transformers';
import type { AppGatewaysWidgetData } from '../models/AppGatewaysResponse';

const dashboardApi = bffWebApi.injectEndpoints({
  endpoints: (build) => ({
    getLegacyServiceReminders: build.query<DashboardServiceRemindersResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/service-reminders`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getServiceReminders: build.query<DashboardServiceRemindersResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/assets-service-reminders`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getRoboticMowersSecondaryStatusCount: build.query<DashboardSecondaryStatusCountResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/robotic-mowers/secondary-status`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getFirmwareUpdates: build.query<DashboardFirmwareUpdatesResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/firmware-updates`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getAttentionList: build.query<AttentionListResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/attention-list`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    machinesStatusCount: build.query<AggregatedCountsResponse<InventoryStatusEnum>, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/machines/status`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    directConnectStatusCount: build.query<AggregatedCountsResponse<OnlineStateEnum>, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/direct-connect`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    machinesSubcategoriesCount: build.query<AggregatedCountsResponse<string>, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/machines/subcategories`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getLastSeenSummary: build.query<DashboardLastSeenSummaryResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/last-seen`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getSettings: build.query<DashboardSettingsResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/settings`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    saveSettings: build.mutation<void, UpdateDashboardSettingsRequestBody>({
      query: (body) => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/settings`,
        method: 'PUT',
        body,
      }),
    }),
    getAppGateways: build.query<AppGatewaysWidgetData, void>({
      transformResponse: transformAppGatewaysChartData,
      query: () => ({
        url: '/dashboard/gateway',
        method: 'GET',
      }),
    }),

    userNpsFeedback: build.mutation<NpsResponse, UserNpsFeedback>({
      query: (body) => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/nps`,
        method: 'POST',
        body,
      }),
    }),
    getMachinesOperationalStatusCount: build.query<MachineOperationalStatusCountResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/machines/operational-status`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getMachinesSecondaryStatusCountV2: build.query<DashboardSecondaryStatusCountResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/v2/machines/secondary-status`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getBatterySecondaryStatusCountV2: build.query<DashboardSecondaryStatusCountResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/v2/battery/secondary-status`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
    getGatewaySecondaryStatusCountV2: build.query<DashboardSecondaryStatusCountResponse, void>({
      query: () => ({
        url: `${process.env.ENDPOINT_BFF}/dashboard/v2/gateway/secondary-status`,
      }),
      providesTags: [TagTypes.Dashboard],
    }),
  }),
});

export const {
  useGetAttentionListQuery,
  useGetAppGatewaysQuery,
  useGetFirmwareUpdatesQuery,
  useGetServiceRemindersQuery,
  useGetRoboticMowersSecondaryStatusCountQuery,
  useGetLastSeenSummaryQuery,
  useLazyGetLastSeenSummaryQuery,
  useGetSettingsQuery,
  useLazyGetSettingsQuery,
  useSaveSettingsMutation,
  useLazyGetAttentionListQuery,
  useLazyGetFirmwareUpdatesQuery,
  useGetLegacyServiceRemindersQuery,
  useLazyGetAppGatewaysQuery,
  useLazyGetLegacyServiceRemindersQuery,
  useLazyGetServiceRemindersQuery,
  useLazyGetRoboticMowersSecondaryStatusCountQuery,
  useUserNpsFeedbackMutation,
  useGetMachinesOperationalStatusCountQuery,
  useGetMachinesSecondaryStatusCountV2Query,
  useGetBatterySecondaryStatusCountV2Query,
  useGetGatewaySecondaryStatusCountV2Query,
} = dashboardApi;
export default dashboardApi;
